import { useState } from "react";

export interface UseColumnVisibilityResult<TKey extends string> {
  readonly visibleColumns: ReadonlySet<TKey>;
  readonly isVisible: (key: TKey) => boolean;
  readonly toggle: (key: TKey) => void;
}

/**
 * Tracks which columns are currently rendered. Initial set seeds the
 * default; `toggle` flips membership for one key.
 */
export function useColumnVisibility<TKey extends string>(
  initial: ReadonlySet<TKey>,
): UseColumnVisibilityResult<TKey> {
  const [visibleColumns, setVisibleColumns] =
    useState<ReadonlySet<TKey>>(initial);

  const isVisible = (key: TKey) => visibleColumns.has(key);

  const toggle = (key: TKey) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  return { visibleColumns, isVisible, toggle };
}
